import { useEffect, useMemo } from 'react'
import { saveAs } from 'file-saver'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

// UI
import { Box, Button, Flex, Heading, Spacer } from '@chakra-ui/react'

// Component

import { authSelector } from '@/stores/auth/authSlice'

import { ErrorSection } from '@/components/Elements'

//API
import useUser from '@/features/users/useUser'

//Constant
import { ContributorGroupType } from '@/features/contributorGroups/constants'
import { UserRole } from '@/features/users/constants'

//Helper
import { createExcelBlob } from '@/utils/fileHelper'
import { isNullish, isNumber } from '@/utils/helper'

import { useInitialTableState } from '@/components/Table/components/useInitialTableState'
import FullTable from '@/components/Table/FullTable/FullTable'
import { convertTableStateToQuery } from '@/utils/tableHelper'
import { createColumnHelper } from '@tanstack/react-table'

import useVendor from '@/features/vendors/useVendor'
import useOfferList from '../../hooks/useOfferList'
import { getOfferSourcingDecisionTableColumns } from './offerSourcingDecisionListColumns'
import { useLocation } from 'react-router-dom'
import { useSearchParams } from 'react-router-dom'

const OfferSourcingDecisionListPage = () => {
  const { user: currentUser } = useSelector(authSelector)
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const workflowStatus = searchParams.get('workflowStatus')
  const initialFilter = useMemo(() => {
    const columnFilters = []
    if (workflowStatus) {
      columnFilters.push({ id: 'workflowStatus', value: workflowStatus.split(',') })
    }

    if (columnFilters.length === 0) {
      return {}
    }

    return { columnFilters: columnFilters }
  }, [workflowStatus])

  // Local State
  const [storedTableState, setStoredTableState] = useInitialTableState(`table_state:offerSourcingDecision`, {})

  const query = convertTableStateToQuery(storedTableState)

  // Hooks
  const { useFetchAllUsers, useUserOptions } = useUser()
  const { useVendorOptions } = useVendor()

  const { useFetchOfferSourcingDecisions } = useOfferList()
  const { data: offerSourcingDecisionResponse, isLoading } = useFetchOfferSourcingDecisions(query)
  const { data: allOfferSourcingDecisionResponse } = useFetchOfferSourcingDecisions({
    page: 1,
    pageSize: 0,
    filters: query.filters,
  })

  const { data: fetchUsersResponse } = useFetchAllUsers({
    page: 1,
    pageSize: 0,
    sortBy: 'fullName',
    orderBy: 1,
    filters: { contributorGroup: ContributorGroupType.PROCUREMENT_OFFICER },
  })

  const { data: fetchAllUsersResponse } = useFetchAllUsers({
    page: 1,
    pageSize: 0,
    sortBy: 'fullName',
    orderBy: 1,
    filters: { status: 'active' },
  })

  //Translated
  const allSaleUsers = fetchAllUsersResponse?.data
    ? fetchAllUsersResponse?.data.filter((user) =>
        [ContributorGroupType.TECHNICAL_PROJECT_MANAGER, 'sales', 'engineer'].includes(user.contributorGroup)
      )
    : []
  const { data: vendorOptions } = useVendorOptions({ status: 'active', valueKey: 'name' })
  const { data: userOptions } = useUserOptions({ status: 'active' })
  const { data: sourcingUserOptions } = useUserOptions({
    status: 'active',
    contributorGroup: ContributorGroupType.PROCUREMENT_OFFICER,
  })
  const { data: salesUserOptions } = useUserOptions({
    labelKey: 'fullName',
    contributorGroup: [
      ContributorGroupType.TECHNICAL_PROJECT_MANAGER,
      ContributorGroupType.SALES,
      ContributorGroupType.ENGINEER,
    ],
  })

  //TODO: Check

  const navigate = useNavigate()
  const columnHelper = createColumnHelper()

  const handleGotoOffer = (row) => {
    const { _id, costBudgetIndex } = row.original
    if (isNullish(_id) || isNullish(costBudgetIndex)) {
      return
    }
    let link = `/offers/${_id}`

    if (isNumber(costBudgetIndex)) {
      link += `?costBudgetIndex=${costBudgetIndex}`
    }
    navigate(link)
  }

  const handleDownload = () => {
    if (!allOfferSourcingDecisionResponse?.data) return
    try {
      const excelData = allOfferSourcingDecisionResponse.data.map((sourcingDecision) => {
        // eslint-disable-next-line no-unused-vars
        const { customerObj, ...rest } = sourcingDecision
        return rest
      })
      const excelBlob = createExcelBlob(excelData)
      saveAs(excelBlob, 'Offer Sourcing Decisions.xlsx')
    } catch (err) {
      console.log(err)
    }
  }

  const defaultColumns = getOfferSourcingDecisionTableColumns({
    salesUserOptions,
    userOptions,
    sourcingUserOptions,
    vendorOptions,
  })

  useEffect(() => {
    if (Object.keys(initialFilter).length > 0) {
      setStoredTableState((prev) => {
        return {
          ...prev,
          ...initialFilter,
        }
      })

      navigate(location.pathname, { replace: true })
    }
  }, [initialFilter])

  if (!currentUser?.fullName) {
    return <>Loading...</>
  }

  if (currentUser.role === UserRole.VENDOR) {
    return <ErrorSection text="You don't have the permission to view this page"></ErrorSection>
  }

  return (
    <>
      <Box shadow="md" borderWidth="1px" bg="white" w="100%" mb="auto">
        <Flex p={5} borderBottom="1px solid #d8dbe0">
          <Heading>Sourcing Decision({offerSourcingDecisionResponse?.count})</Heading>
          <Spacer />

          <Flex gap={1} alignItems={'center'}>
            <Button colorScheme={'green'} onClick={handleDownload}>
              Download
            </Button>
          </Flex>
        </Flex>
        <Box p={5} overflow={'scroll'}>
          <FullTable
            data={offerSourcingDecisionResponse?.data ? offerSourcingDecisionResponse.data : []}
            rowCount={offerSourcingDecisionResponse?.count}
            columnDef={defaultColumns}
            state={storedTableState}
            setState={setStoredTableState}
            handleRowClick={handleGotoOffer}
          />
        </Box>
      </Box>
    </>
  )
}

export default OfferSourcingDecisionListPage
